// src/context/TextContext.js
// Provides multilanguage text lookup throughout the app.
// Usage:
//   const { t } = useTexts();
//   t(TXT.SAVE)  →  "Guardar" or "Save" depending on user's language
//
// Texts are loaded once after login from GET /api/auth/texts.
// The user's language (Usr_idioma) is determined server-side.
// Future runtime language switching: call reloadTexts(idiId).

import React, { createContext, useContext, useState, useCallback } from 'react';
import { authApi } from '../services/api';

// ---------------------------------------------------------------------------
// Text ID constants — mirrors WHL_TXT_Texts.txt_id
// ---------------------------------------------------------------------------
export const TXT = {
  WELCOME:           1,
  NEW:               2,
  UPDATE:            3,
  SAVE:              4,
  CANCEL:            5,
  DELETE:            6,
  CONFIRMATION:      8,
  CONFIRM:           9,
  LOGIN_TITLE:       10,
  USER_LABEL:        11,
  PASSWORD_LABEL:    12,
  ENTER_USER:        13,
  PASSWORD:          14,
  FILES_TAB:         15,
  DOWNLOAD:          16,
  FILE_LABEL:        17,
  DESCRIPTION_LABEL: 18,
  VERSIONS:          19,
  UPLOAD:            21,
  WRONG_CREDENTIALS: 23,
  VIEW:              24,
  WARNING:           25,
  OK:                26,
  LOAD_MORE:         27,
  BACK:              28,
  TOTAL:             29,
  AREA_LABEL:        30,
  STATUS_LABEL:      31,
  FILE_SAVED:        32,
  TRANSACTION_ERROR: 33,
  FILE_REMOVED:      35,
  OLD_PASSWORD:      36,
  NEW_PASSWORD:      37,
  REPEAT_PASSWORD:   38,
  WRONG_PASSWORDS:   40,
  FIELD:             43,
  MANDATORY:         44,
  DATA_SAVED:        47,
  DATA_UPDATED:      48,
  SEARCH:            52,
  PASSWORD_CHANGED:  56,
  RECORD_DELETED:    60,
  EDITION_CANCELLED: 61,
  ERROR_SAVING_DOC:  62,
  RECOVER_PASSWORD:  49,
  SUBMIT:            64,
  CHANGE_PASSWORD:   65,
  SIGN_OUT:          66,
  RUN_QUERY:         67,
  COMMENTS_TAB:      68,
  POST:              69,
  CONFIRM_CANCEL:    71,
  CONFIRM_DELETE:    72,
  UPDATE_DESC:       73,
  ADD:               74,
  KEYWORDS_TAB:      75,
  APP_TITLE:         76,
  TITLE_LABEL:       77,
  DATE_FROM:               78,
  DATE_TO:                 79,
  INCLUDE_KEYWORDS:        80,
  CONFIRM_DELETE_RECEIPTS: 81,
  RECEIPTS_TITLE:          82,
  SELECT_BATCH:            83,
  PROCESS:                 84,
  PUBLISH:                 85,
  PROCESSING:              86,
  PUBLISHING:              87,
  DELETING:                88,
  CUIL:                    89,
  LEGAJOS:                 90,
  PAGES:                   91,
  RECORD:                  92,
  RECORDS_PUBLISHED:       93,
  RECORDS_DELETED:         94,
  MENTION_HINT:            95,
  NO_DOCUMENTS_FOUND:      96,
  NEW_DOCUMENT:            97,
  DOC_TYPE_LABEL:          98,
  NOTIFICATIONS:           99,
  // Extras usados como fallback (ya existen en DB)
  UPLOAD_NEW_VERSION:      22,
  WELCOME_MAIL_SENT:       59,
  MAIL_SENT:               57,
  NOTIFICATION_SENT:       100,
  COMPLETED:               63,
  INVALID_FILE_NAME:       70,
  TIMEZONE:                101,
  REMEMBER_ME:             102,
  CURRENT_TIME_IN_ZONE:    103,
  USUARIOS:                104,
  PARAMETRIZACION:         105,
  DOWNLOAD_ALL:            106,
  USUARIO_LABEL:           107,
  APELLIDO_NOMBRE:         108,
  MAIL_LABEL:              109,
  IDIOMA_LABEL:            110,
  GRUPO_USUARIOS:          111,
  PERFIL_FUNCIONES:        112,
  SIN_PERFIL:              113,
  LOGIN_BRAND_TAGLINE:     114,
  LOGIN_BRAND_MSG:         115,
  DISABLE_USER:            116,
  ENABLE_USER:             117,
  VIEW_CARDS:              118,
  VIEW_GRID:               119,
  VIEW_CALENDAR:           120,
  LOADING_MORE:            121,
  VIEW_ALL:                122,
  LINK_COPIED:             123,
  GENERATE_AND_COPY:       124,
  SETTINGS:                125,
  PREFERENCES:             126,
  SUBSCRIPTIONS:           127,
  TIME_ZONE:               128,
  LANGUAGE:                129,
  NEW_SUBSCRIPTION:        130,
  DATA_UPDATED_SUB:        131,
  FILES_UPDATED:           132,
  NO_SUBSCRIPTIONS:        133,
  DATA_LABEL:              134,
  FILES_LABEL:             135,
  SELECT_ONE_OPTION:       136,
  ERROR_LOADING_SUBS:      137,
  DOC_TYPE_LABEL2:         138,
  NOTIFY_WHEN:             139,
  ASSISTANT:               140,
  EXPERT_MODE:             141,
  ASSISTANT_PLACEHOLDER:   142,
  SEND:                    143,
  ASSISTANT_ERROR:         144,
  ASSISTANT_WELCOME:       145,
  ASSISTANT_WELCOME_EXP:   146,
  DOCS_LOAD_ERROR:         147,
  USER_CREATED_PWD:        148,
  CSV_DOWNLOAD_ERROR:      149,
};

const TextContext = createContext(null);

export function TextProvider({ children }) {
  const [texts, setTexts] = useState({});
  const [idiId, setIdiId] = useState(null);

  // Load texts using browser language — called on app start, no auth needed
  const loadPublicTexts = useCallback(async () => {
    try {
      const browserLang = navigator.language?.substring(0, 2) || '';
      const res = await authApi.textsPublic(browserLang);
      setTexts(res.data.texts || {});
      setIdiId(res.data.idiId || null);
    } catch (err) {
      console.warn('Could not load public texts:', err.message);
    }
  }, []);

  // Load texts for the authenticated user's language (Usr_idioma) — called after login
  const loadTexts = useCallback(async () => {
    try {
      const res = await authApi.texts();
      setTexts(res.data.texts || {});
      setIdiId(res.data.idiId || null);
    } catch (err) {
      console.warn('Could not load texts:', err.message);
    }
  }, []);

  // t(id) — returns the text for the given txt_id, or fallback if not found
  const t = useCallback((id, fallback = '') => {
    return texts[id] ?? fallback;
  }, [texts]);

  return (
    <TextContext.Provider value={{ t, idiId, loadTexts, loadPublicTexts }}>
      {children}
    </TextContext.Provider>
  );
}

export function useTexts() {
  return useContext(TextContext);
}
